import { languageEnum } from "./global";
import { z } from "zod";

export const relationTypeEnum = z.enum(["client", "supplier", "both"]);

const baseRelationSchema = z.object({
  prefix: z.string({ invalid_type_error: "Prefix must be a string" }).optional().nullable(),
  firstName: z.string({ invalid_type_error: "First name must be a string" }).optional().nullable(),
  lastName: z.string({ invalid_type_error: "Last name must be a string" }).optional().nullable(),
  email: z
    .string({ invalid_type_error: "Email must be a string" })
    .email({ message: "Email must be a valid email address" })
    .or(z.literal(""))
    .optional()
    .nullable(),
  type: relationTypeEnum,
  isOrganization: z.boolean({ invalid_type_error: "Is organization must be a boolean" }),
  organizationName: z.string({ invalid_type_error: "Organization name must be a string" }).optional().nullable(),
  organizationTax: z.string({ invalid_type_error: "Organization tax must be a string" }).optional().nullable(),
  addressLine1: z.string({ invalid_type_error: "Address line 1 must be a string" }).optional().nullable(),
  addressLine2: z.string({ invalid_type_error: "Address line 2 must be a string" }).optional().nullable(),
  zipcode: z.string({ invalid_type_error: "Zipcode must be a string" }).optional().nullable(),
  state: z.string({ invalid_type_error: "State must be a string" }).optional().nullable(),
  city: z.string({ invalid_type_error: "City must be a string" }).optional().nullable(),
  country: z.string({ invalid_type_error: "Country must be a string" }).optional().nullable(),
  currency: z
    .string({ invalid_type_error: "Currency must be a string" })
    .min(1, { message: "Currency cannot be empty" }),
  paymentTerm: z
    .number({ invalid_type_error: "Payment term must be a number" })
    .int({ message: "Payment term must be an integer" })
    .optional()
    .nullable(),
  note: z.string({ invalid_type_error: "Note must be a string" }).optional().nullable(),
});

export const createRelationSchema = baseRelationSchema
  .refine(
    (data) => {
      if (data.isOrganization && (!data.organizationName || data.organizationName.trim() === "")) {
        return false;
      }
      return true;
    },
    {
      message: "Organization name is required when relation is a organization",
      path: ["organizationName"],
    },
  )
  .refine(
    (data) => {
      // If type is NOT supplier OR isOrganization is false, then firstName, lastName, and email are required
      if (!data.isOrganization) {
        if (!data.firstName || data.firstName.trim() === "") {
          return false;
        }
      }
      return true;
    },
    {
      message: "First name is required",
      path: ["firstName"],
    },
  )
  .refine(
    (data) => {
      // If type is NOT supplier OR isOrganization is false, then firstName, lastName, and email are required
      if (!data.isOrganization) {
        if (!data.lastName || data.lastName.trim() === "") {
          return false;
        }
      }
      return true;
    },
    {
      message: "Last name is required",
      path: ["lastName"],
    },
  )
  .refine(
    (data) => {
      // If type is NOT supplier OR isOrganization is false, then firstName, lastName, and email are required
      if (!data.isOrganization) {
        if (!data.email || data.email.trim() === "") {
          return false;
        }
      }
      return true;
    },
    {
      message: "Email is required",
      path: ["email"],
    },
  );

export const updateRelationSchema = baseRelationSchema
  .partial()
  .extend({
    id: z.string({ invalid_type_error: "ID must be a string" }),
  })
  .refine(
    (data) => {
      if (data.isOrganization && (!data.organizationName || data.organizationName.trim() === "")) {
        return false;
      }
      return true;
    },
    {
      message: "Organization name is required when relation is a organization",
      path: ["organizationName"],
    },
  )
  .refine(
    (data) => {
      // If type is NOT supplier OR isOrganization is false, then firstName, lastName, and email are required
      if (!data.isOrganization) {
        if (data.firstName !== undefined && (!data.firstName || data.firstName.trim() === "")) {
          return false;
        }
      }
      return true;
    },
    {
      message: "First name is required",
      path: ["firstName"],
    },
  )
  .refine(
    (data) => {
      // If type is NOT supplier OR isOrganization is false, then firstName, lastName, and email are required
      if (!data.isOrganization) {
        if (data.lastName !== undefined && (!data.lastName || data.lastName.trim() === "")) {
          return false;
        }
      }
      return true;
    },
    {
      message: "Last name is required",
      path: ["lastName"],
    },
  )
  .refine(
    (data) => {
      // If type is NOT supplier OR isOrganization is false, then firstName, lastName, and email are required
      if (!data.isOrganization) {
        if (data.email !== undefined && (!data.email || data.email.trim() === "")) {
          return false;
        }
      }
      return true;
    },
    {
      message: "Email is required",
      path: ["email"],
    },
  );

export const getRelationSchema = z.object({
  id: z.string({ invalid_type_error: "ID must be a string" }),
});

export const deleteRelationSchema = z.object({
  id: z.string({ invalid_type_error: "ID must be a string" }),
});

// Filter params for listing relations
export const getRelationsParamsSchema = z.object({
  // Text search filters
  name: z.string().optional(),
  email: z.string().optional(),
  // Option filters
  type: z
    .union([relationTypeEnum, z.array(relationTypeEnum)])
    .optional()
    .transform((val) => (val ? (Array.isArray(val) ? val : [val]) : undefined)),
  // Number range filters
  paymentTermMin: z.coerce.number().optional(),
  paymentTermMax: z.coerce.number().optional(),
  // Date range filters
  createdAtFrom: z.coerce.date().optional(),
  createdAtTo: z.coerce.date().optional(),
});

export type ZodCreateRelationSchema = z.infer<typeof createRelationSchema>;
export type ZodUpdateRelationSchema = z.infer<typeof updateRelationSchema>;
export type ZodGetRelationSchema = z.infer<typeof getRelationSchema>;
export type ZodDeleteRelationSchema = z.infer<typeof deleteRelationSchema>;
export type ZodGetRelationsParams = z.infer<typeof getRelationsParamsSchema>;
export type RelationType = z.infer<typeof relationTypeEnum>;

export const relationSchema = z.object({
  id: z.string(),
  prefix: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  type: relationTypeEnum,
  isOrganization: z.boolean(),
  organizationName: z.string().nullable(),
  organizationTax: z.string().nullable(),
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  zipcode: z.string().nullable(),
  state: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  currency: z.string(),
  language: languageEnum,
  paymentTerm: z.number(),
  note: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  organizationId: z.string(),
  userId: z.string(),
});

export type RelationSchema = z.infer<typeof relationSchema>;

export const createRelationSchemaDefaultValues: ZodCreateRelationSchema = {
  prefix: "",
  firstName: "",
  lastName: "",
  email: "",
  type: "client",
  isOrganization: false,
  organizationName: "",
  organizationTax: "",
  addressLine1: "",
  addressLine2: "",
  zipcode: "",
  state: "",
  city: "",
  country: "",
  currency: "",
  paymentTerm: 30,
  note: "",
};
